import React from "react";
import { Box, Container, Typography, Link, List, ListItem, Paper, Divider, Alert } from "@mui/material";

/** Helper section wrapper */
const Section = ({ id, title, children }: { id: string; title: string; children: React.ReactNode }) => {
    return (
        <Box id={id} sx={{ scrollMarginTop: 96 }}>
            <Typography variant="h5" component="h2" fontWeight={700} gutterBottom>
                {title}
            </Typography>
            <Box sx={{ color: "text.primary" }}>{children}</Box>
        </Box>
    );
};

const Privacy: React.FC = () => {
    const lastUpdated = "October 23, 2025";
    const contactEmail = "miyu.apps@gmail.com";

    return (
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
            <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h3" component="h1" fontWeight={700} gutterBottom>
                        Privacy Policy
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last updated: {lastUpdated}
                    </Typography>
                </Box>

                <Alert severity="info" sx={{ mb: 3 }}>
                    Your privacy matters to us. This policy explains what data we collect, how we use it, and what rights you have.
                </Alert>

                {/* Table of Contents */}
                <Box component="nav" aria-label="Privacy Policy table of contents" sx={{ mb: 4 }}>
                    <Typography variant="overline" color="text.secondary">
                        Contents
                    </Typography>
                    <List dense sx={{ listStyleType: "decimal", pl: 3 }}>
                        {[
                            ["who-we-are", "Who We Are"],
                            ["information-we-collect", "Information We Collect"],
                            ["how-we-use", "How We Use Your Information"],
                            ["legal-basis", "Legal Basis"],
                            ["storage-and-security", "Data Storage and Security"],
                            ["sharing", "Sharing Your Information"],
                            ["rights", "Your Rights (GDPR)"],
                            ["cookies", "Cookies and Analytics"],
                            ["advertising", "Advertising"],
                            ["retention", "Retention"],
                            ["updates", "Updates to This Policy"],
                            ["contact", "Contact"],
                        ].map(([id, label]) => (
                            <ListItem key={id} sx={{ display: "list-item", py: 0 }}>
                                <Link href={`#${id}`} underline="hover" variant="body2">
                                    {label}
                                </Link>
                            </ListItem>
                        ))}
                    </List>
                </Box>

                <Section id="who-we-are" title="1. Who We Are">
                    <Typography>
                        CooCoo is operated by a private individual in the Netherlands. For privacy questions or concerns, contact us at{" "}
                        <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="information-we-collect" title="2. Information We Collect">
                    <Typography sx={{ mb: 1 }}>When you use CooCoo, we collect:</Typography>
                    <ul>
                        <li>Email, username, and password when you create an account.</li>
                        <li>Content you share such as recipes, posts, and images.</li>
                        <li>Technical data (like IP address, browser type, usage patterns).</li>
                        <li>Cookies to remember preferences and keep you logged in.</li>
                    </ul>
                    <Typography>We never sell your data or track you across other websites.</Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="how-we-use" title="3. How We Use Your Information">
                    <Typography sx={{ mb: 1 }}>We use your data to:</Typography>
                    <ul>
                        <li>Operate and improve CooCoo’s features.</li>
                        <li>Keep your account secure and functional.</li>
                        <li>Display and share your posts publicly (if you choose to post).</li>
                        <li>Communicate with you about updates, security, or policy changes.</li>
                        <li>(In the future) show privacy-friendly advertisements.</li>
                    </ul>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="legal-basis" title="4. Legal Basis">
                    <Typography>
                        Under the GDPR, we process your personal data based on: (1) contract (to provide CooCoo services), (2) consent (for
                        optional cookies or communications), and (3) legitimate interest (to maintain and improve our platform).
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="storage-and-security" title="5. Data Storage and Security">
                    <Typography>
                        Your data is securely stored on Vercel (frontend) and Railway (backend). We use encryption, access controls, and
                        monitoring to protect your information. While no system is perfect, we take your privacy seriously.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="sharing" title="6. Sharing Your Information">
                    <Typography sx={{ mb: 1 }}>We may share minimal data with trusted providers to operate CooCoo:</Typography>
                    <ul>
                        <li>Hosting and infrastructure providers (Vercel, Railway).</li>
                        <li>Legal authorities if required by law.</li>
                    </ul>
                    <Typography>We never share your data with advertisers or unrelated third parties.</Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="rights" title="7. Your Rights (GDPR)">
                    <Typography sx={{ mb: 1 }}>You have the right to:</Typography>
                    <ul>
                        <li>Access and obtain a copy of your data.</li>
                        <li>Request correction or deletion of your data.</li>
                        <li>Withdraw consent at any time (for cookies or communications).</li>
                        <li>Request data portability.</li>
                        <li>File a complaint with your local data protection authority.</li>
                    </ul>
                    <Typography>
                        You can contact <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link> to exercise these rights.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="cookies" title="8. Cookies and Analytics">
                    <Typography>
                        CooCoo uses essential cookies for login and performance. If analytics are added later, they will be privacy-friendly
                        (like Plausible or another GDPR-compliant service) and described here.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="advertising" title="9. Advertising">
                    <Typography>
                        CooCoo may introduce ads in the future. Any advertising partners will comply with GDPR and will not collect or sell
                        your personal data without consent.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="retention" title="10. Retention">
                    <Typography>
                        We keep your data as long as you have an account. If you delete your account, we remove your personal data within
                        about 30 days, except where required by law.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="updates" title="11. Updates to This Policy">
                    <Typography>
                        We may update this Privacy Policy from time to time. If changes are significant, we’ll notify you by email or
                        through the app before they take effect.
                    </Typography>
                </Section>

                <Divider sx={{ my: 3 }} />

                <Section id="contact" title="12. Contact">
                    <Typography>
                        Questions, concerns, or privacy requests? Email us at <Link href={`mailto:${contactEmail}`}>{contactEmail}</Link>.
                    </Typography>
                </Section>

                <Divider sx={{ my: 4 }} />

                <Box>
                    <Typography variant="h6" gutterBottom>
                        Thank You 💛
                    </Typography>
                    <Typography>We appreciate your trust and will always handle your information with care and transparency.</Typography>
                </Box>
            </Paper>
        </Container>
    );
};

export default Privacy;
